import React from 'react'
import { MenuFolder } from './menufolder';
import { MenuItem } from './menuitem';
import './menu.css';

export interface IMenu
{
    desc      : string;
    to?       : string;
    children? : IMenu[];
}

interface State {
    active : string;
}

interface Props {
    children  : IMenu[];
    closeMenu?: () => void;
    onRegisterCloseSubmenus?: (fn: () => void) => void;
    onBeforeOpen?: () => void;
}

export class Menu extends React.Component<Props, State> 
{
    private menuMainRef = React.createRef<HTMLUListElement>();
    private submenuRef = React.createRef<HTMLUListElement>();
    private itemRefs = new Map<string, HTMLLIElement>();

    constructor(props : Props)
    {
        super(props);

        this.state = {
            active : ''
        }
    }

    componentDidMount()
    {
        this.props.onRegisterCloseSubmenus?.(() => this.setState({ active : '' }));
    }

    private positionSubmenu()
    {
        const mainEl = this.menuMainRef.current;
        const subEl = this.submenuRef.current;
        if (!mainEl || !subEl) return;

        const activeId = this.state.active;
        if (activeId === '') return;

        const activeEl = this.itemRefs.get(activeId);
        if (!activeEl) return;

        subEl.style.top = `${(activeEl.offsetTop/16)}rem`;
    }

    componentDidUpdate(prevProps: Props, prevState: State)
    {
        if (prevState.active !== this.state.active) {
            this.positionSubmenu();
        }
    }

    createID(parentID : string, childIndex : number)
    {
        if (parentID.length > 0) {
            return `${parentID}.${childIndex}`;
        }
        else {
            return `${childIndex}`;
        }
    }

    handleSelect()
    {
        this.setState({ active : '' });
        this.props.closeMenu?.();
    }

    handleShowFolder(id : string, show : boolean)
    {
        if (show) {
            this.props.onBeforeOpen?.();
            this.setState({ active : id });
        }
        else {
            this.setState({ active : '' });
        }
    }

    findActiveChildren() : IMenu[] | null
    {
        if (this.state.active === '') return null;

        const parts = this.state.active.split('.');
        let items = this.props.children;

        for (const part of parts) {
            const index = parseInt(part);
            if (index >= 0 && index < items.length) {
                const item = items[index];
                if (item.children) {
                    items = item.children;
                } else {
                    return null;
                }
            } else {
                return null;
            }
        }

        return items;
    }

    isInActiveChain(id : string) : boolean
    {
        return id === this.state.active || this.state.active.startsWith(id + '.');
    }

    render() {
        const activeChildren = this.findActiveChildren();

        return (
            <div className="menu">
                <ul className="menu-desktop">
                    { this.renderChildren("", this.props.children) }
                </ul>
                <ul className="menu-main" ref={this.menuMainRef}>
                    { this.renderMainChildren("", this.props.children) }
                </ul>
                { activeChildren && (
                    <ul className="menu-sub" ref={this.submenuRef}>
                        { this.renderChildren(this.state.active, activeChildren) }
                    </ul>
                )}
            </div>
        );
    }

    renderMainChild(id : string, child : IMenu) : React.JSX.Element
    {
        if (child.children) {
            return (
                <li key={id} ref={el => { if (el) this.itemRefs.set(id, el); }}
                    className={`menu-item${this.isInActiveChain(id) ? ' show' : ''}`}
                    onClick={() => this.handleShowFolder(id, id !== this.state.active)}>
                    <div className="menu-link">
                        {child.desc}
                        <span className="menu-arrow">›</span>
                    </div>
                </li>
            );
        }
        else {
            return (<MenuItem key={ id } to={child.to!} children={child.desc} onSelect={() => this.handleSelect() } />);
        }
    }

    renderMainChildren(parentID : string, children : IMenu[]) : React.JSX.Element[]
    {
        let elements : React.JSX.Element[] = [];

        for (let i = 0; i < children.length; i++) {
            const id = this.createID(parentID, i);
            elements.push(this.renderMainChild(id, children[i]));
        }

        return elements;
    }

    renderChild(id : string, child : IMenu) : React.JSX.Element
    {
        if (child.children) {
            return (
                <MenuFolder key={id} id={id} desc={child.desc} showFolder={(id, show) => this.handleShowFolder(id, show)} show={ id === this.state.active }>
                    { this.renderChildren(id, child.children) }
                </MenuFolder>
            );
        }
        else {
            return (<MenuItem key={ id } to={child.to!} children={child.desc} onSelect={() => this.handleSelect() } />);
        }
    }

    renderChildren(parentID : string, children : IMenu[]) : React.JSX.Element[]
    {
        let elements : React.JSX.Element[] = [];

        for (let i = 0; i < children.length; i++) {
            const id = this.createID(parentID, i);
            elements.push(this.renderChild(id, children[i]));
        }

        return elements;
    }
}
